import { Checkbox } from '@latitude/checkbox';
import { cn } from '@latitude/token-utils';
import { useActionListContext } from 'ActionListContext';
import { useCallback, useMemo } from 'react';
import type { CommandSelectAllProps } from 'types';
import { commandItemVariants } from 'variants/item-variants';

export const CommandSelectAll = ({
  itemSize,
  label,
}: CommandSelectAllProps) => {
  const {
    selectedValues,
    optionMap,
    isMulti,
    allowSelectAll,
    onSetValues,
    filteredOptions,
  } = useActionListContext();

  const selectableOptions = useMemo(
    () => Array.from(optionMap.values()).filter((option) => !option.disabled),
    [optionMap],
  );

  const selectedArray: string[] = useMemo(
    () => (Array.isArray(selectedValues) ? selectedValues : []),
    [selectedValues],
  );

  const disabledSelectedOptions = useMemo(
    () =>
      Array.from(optionMap.values())
        .filter(
          (option) => option.disabled && selectedArray.includes(option.value),
        )
        .map((option) => option.value),
    [optionMap, selectedArray],
  );

  const areAllSelected = useMemo(
    () =>
      selectedArray.length > 0 &&
      selectableOptions.length > 0 &&
      selectableOptions.every((option) => selectedArray.includes(option.value)),
    [selectedArray, selectableOptions],
  );

  const isSomeSelected = useMemo(
    () => selectedArray.length > 0 && !areAllSelected,
    [selectedArray, areAllSelected],
  );

  const onSelectAllClick = useCallback(() => {
    if (areAllSelected) {
      // Deselect all, but keep disabled+selected items
      onSetValues(disabledSelectedOptions);
      return;
    }

    // Select all selectable items + keep disabled+selected
    onSetValues([
      ...disabledSelectedOptions,
      ...selectableOptions.map((option) => option.value),
    ]);
  }, [areAllSelected, onSetValues, disabledSelectedOptions, selectableOptions]);

  if (!isMulti || !allowSelectAll || filteredOptions.length === 0) {
    return null;
  }

  const resolvedLabel =
    typeof label === 'function'
      ? label({
          selected: selectedArray.length,
          total: optionMap.size,
          filteredTotal: filteredOptions.length,
          allSelected: areAllSelected,
        })
      : (label ?? `${selectedArray.length} of ${optionMap.size}`);

  return (
    <Checkbox
      id="select-all"
      wrapperProps={{ role: 'option' }}
      wrapperClassname={cn(
        commandItemVariants({
          size: itemSize,
        }),
        'mb-spacing-comp-popover-gap',
      )}
      data-testid="action-list-select-all-checkbox"
      checked={areAllSelected}
      indeterminate={isSomeSelected}
      label={resolvedLabel}
      onCheckedChange={onSelectAllClick}
    />
  );
};
